import React, { useEffect, useState, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { getSupabaseClient } from "../lib/supabase";

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
  badgeKey?: string;
};

const navItems: NavItem[] = [
  {
    label: "Guides",
    to: "/admin/guides",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 21v-2a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M20 8v6" />
        <path d="M23 11h-6" />
      </svg>
    ),
  },
  {
    label: "Seekers",
    to: "/admin/seekers",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M3 21v-1a6 6 0 0 1 6-6h6a6 6 0 0 1 6 6v1" />
      </svg>
    ),
  },
  {
    label: "Events",
    to: "/admin/events",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    label: "Alerts",
    to: "/admin/alerts",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  {
    label: "Resources",
    to: "/admin/resources",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20" />
        <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
        <path d="M8 6h8" />
      </svg>
    ),
  },
  {
    label: "Admin Users",
    to: "/admin/users",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Terms Builder",
    to: "/admin/terms",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    label: "Notifications",
    to: "/admin/notifications",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    label: "Messages",
    to: "/admin/messages",
    badgeKey: "messages",
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

type AdminSidebarProps = {
  mobileOpen?: boolean;
  onClose?: () => void;
};

export function AdminSidebar({ mobileOpen = false, onClose }: AdminSidebarProps) {
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const supabase = useMemo(() => getSupabaseClient(), []);

  useEffect(() => {
    let cancelled = false;

    const fetchUnread = async () => {
      try {
        const { data, error } = await supabase
          .from("support_conversations")
          .select("admin_unread_count");

        if (!error && data && !cancelled) {
          const total = data.reduce(
            (sum: number, row: { admin_unread_count: number }) => sum + (row.admin_unread_count || 0),
            0
          );
          setSupportUnreadCount(total);
        }
      } catch {
        // ignore
      }
    };

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [supabase]);

  const sidebarContent = (
    <>
      <div className="border-b border-[#FEF6D6]/10 px-6 py-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FEF6D6]/50">
            Admin
          </p>
          <h2 className="mt-2 text-xl font-medium text-[#FEF6D6] font-recoleta">
            Control Center
          </h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg text-[#FEF6D6]/70 hover:text-[#FEF6D6] hover:bg-[#403851] transition-colors"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              [
                "group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-[#FFFCEF] text-[#2C2731] shadow-sm"
                  : "text-[#FEF6D6]/80 hover:bg-[#403851] hover:text-[#FEF6D6]",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={[
                    "relative flex h-9 w-9 items-center justify-center rounded-lg border transition-colors",
                    isActive
                      ? "border-transparent bg-[#2C2731] text-[#FEF6D6]"
                      : "border-transparent bg-[#FFFCEF]/10 text-[#FEF6D6]/80 group-hover:bg-[#FFFCEF]/15 group-hover:text-[#FEF6D6]",
                  ].join(" ")}
                >
                  {item.icon}
                  {item.badgeKey === "messages" && supportUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                      {supportUnreadCount > 9 ? "9+" : supportUnreadCount}
                    </span>
                  )}
                </span>
                <span className="flex-1">{item.label}</span>
                {item.badgeKey === "messages" && supportUnreadCount > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                    {supportUnreadCount > 99 ? "99+" : supportUnreadCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 xl:w-72 flex-col border-r border-[#FEF6D6]/10 bg-[#2C2731] text-[#FEF6D6]">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-[#2C2731] text-[#FEF6D6] shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
